import { useEffect, type RefObject } from "react";

/**
 * Fechar clicando fora, fechar com Esc, ou os dois.
 *
 * Onze lugares escreviam esse par de listeners à mão — menu de conta,
 * seletor de empresa, uploader de avatar, painel de filtros, caixa do
 * Copiloto, diálogo de confirmação, drawer de menção, modal de
 * justificativas, diálogos de filtro salvo, caixa rápida do Copiloto e
 * o painel de dimensões do dashboard —, cada um com a mesma sequência
 * de `addEventListener` + limpeza. A metade que só fechava no Esc e a
 * que só fechava no clique-fora não eram escolha: eram a cópia que
 * alguém fez com metade do original.
 *
 * **`mousedown`, não `click`.** O clique termina depois que o React já
 * pode ter re-renderizado; escutar o começo do gesto é o que faz o
 * fechamento acontecer antes de o alvo mudar embaixo do cursor.
 *
 * **Sem `ref` nem `within`, só o Esc vale.** É o caso do diálogo e do
 * drawer, que ocupam a tela inteira e têm o próprio backdrop pra tratar
 * o clique.
 *
 * **`within` é a variante por SELETOR**, pro menu que se repete por
 * linha (as ações de cada menção): ali não há um `ref` único a apontar,
 * e o que define "dentro" é o ancestral com aquela classe.
 *
 * O efeito não arma nada quando `active` é falso: um listener global
 * por componente fechado é custo que ninguém vê e que cresce com a
 * tela.
 */
export function useDismiss(
  active: boolean,
  onDismiss: () => void,
  options: {
    ref?: RefObject<HTMLElement | null>;
    within?: string;
    escape?: boolean;
  } = {}
): void {
  const { ref, within, escape = true } = options;

  useEffect(() => {
    if (!active) return;

    const onMouseDown = (event: MouseEvent) => {
      const alvo = event.target as HTMLElement;
      const dentro = within
        ? alvo.closest(within) !== null
        : (ref?.current?.contains(alvo) ?? false);
      if (!dentro) onDismiss();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };

    const ouveClique = Boolean(ref || within);
    if (ouveClique) document.addEventListener("mousedown", onMouseDown);
    if (escape) document.addEventListener("keydown", onKeyDown);
    return () => {
      if (ouveClique) document.removeEventListener("mousedown", onMouseDown);
      if (escape) document.removeEventListener("keydown", onKeyDown);
    };
  }, [active, onDismiss, ref, within, escape]);
}
